import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Volume2 } from "lucide-react";
import { ANTHEM } from "../../content.js";
import { EASE } from "../motion/Reveal.jsx";

const HIGHLIGHTS = [
  ["World-Class", "Strength & Conditioning"],
  ["Asia's", "Strongest Gym"],
  ["One Membership", "Whole Family"],
];

export default function HeroShowcase() {
  const [soundOn, setSoundOn] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (soundOn) a.play().catch(() => {});
    else a.pause();
  }, [soundOn]);

  return (
    <section data-testid="hero-section" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-navy text-white">
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, ease: EASE }}
      >
        <img
          data-testid="hero-swimmer-image"
          src="/oneiros/hero-swimmer.jpg"
          alt="A swimmer training in the Oneiros semi-Olympic pool"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/35 to-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-transparent to-transparent" />
      </motion.div>

      <audio ref={audioRef} src={ANTHEM.audio} loop preload="none" onError={() => { if (audioRef.current && audioRef.current.src.indexOf(ANTHEM.audio) > -1) audioRef.current.src = ANTHEM.src; }} />

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-16 pt-40 lg:px-12 lg:pb-20">
        <p className="eyebrow mb-6 flex items-center gap-3 text-white">
          <span className="inline-block h-[2px] w-10 bg-brand" />
          Fitness Beyond Tomorrow
        </p>
        <h1 data-testid="hero-main-headline" className="font-display text-[clamp(2.8rem,8.5vw,7rem)] leading-[0.98]">
          {["A Club That", "Moves With You."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.25 + i * 0.12, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          className="mt-6 max-w-md text-base leading-relaxed text-white/75 md:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
        >
          Step inside the world of Oneiros — where your journey to a healthier, stronger life begins.
        </motion.p>
        <motion.div
          className="mt-9 flex flex-col gap-4 sm:flex-row"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
        >
          <Link
            data-testid="hero-explore-clubs-btn"
            to="/our-clubs"
            className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-dark"
          >
            Explore Our Clubs
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            data-testid="hero-enquire-btn"
            to="/contact"
            className="inline-flex items-center justify-center rounded-full border border-white/35 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white hover:bg-white hover:text-navy"
          >
            Enquire Now
          </Link>
        </motion.div>

        <motion.div
          className="mt-14 flex flex-col gap-8 border-t border-white/15 pt-7 md:flex-row md:items-end md:justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-10">
            {HIGHLIGHTS.map(([top, bottom]) => (
              <li key={bottom} className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-brand">{top}</span>
                <span className="mt-1 text-sm font-semibold uppercase tracking-[0.14em] text-white/85">{bottom}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-6">
            <button
              data-testid="hero-anthem-sound-toggle"
              onClick={() => setSoundOn((s) => !s)}
              className={`inline-flex items-center gap-3 whitespace-nowrap rounded-full border border-white/25 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.22em] transition-colors duration-300 hover:border-white ${
                soundOn ? "text-brand" : "text-white/80"
              }`}
            >
              {soundOn ? (
                <span className="flex h-3.5 items-end gap-[3px]">
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className="eq-bar h-full w-[3px] bg-brand" />
                  ))}
                </span>
              ) : (
                <Volume2 className="h-3.5 w-3.5" />
              )}
              {soundOn ? "Anthem Playing" : "Play Anthem"}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
